<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogDescription,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, watch } from 'vue';
    import { toast } from 'vue-sonner';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import type { SharedData, Voucher } from '@/types';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { useVoucherStore } from '@/stores/voucher';
    import VoucherMarkAsMissingForm from '@/components/vouchers/VoucherMarkAsMissingForm.vue';
    import type { Table } from '@tanstack/vue-table';

    const props = defineProps<{
        table: Table<Voucher>;
    }>();

    const voucherStore = useVoucherStore();
    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const { t } = useI18n();
    const queryClient = useQueryClient();

    const form = useForm({
        ids: [] as number[],
        account: null,
        comment: null,
    });

    const handleUpload = () => {
        form.post(route('v2.voucher.admin.mark-as-missing.selection'), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(t('vouchers.select.mark_as_missing_responses.success'));
                props.table.resetRowSelection();
                queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId] });
                voucherStore.toggleOpen('mark_as_missing', false);
            },
            onError: (error: any) => {
                if (error instanceof Error) {
                    toast.error(t('forms.system_error'));
                }
            },
        });
    };

    watch(
        () => voucherStore.validVoucherIds,
        (newIds) => {
            form.ids = newIds;
        },
        { immediate: true },
    );
</script>

<template>
    <Dialog
        :open="voucherStore.markAsMissingModalOpen"
        @update:open="(value) => voucherStore.toggleOpen('mark_as_missing', value)"
    >
        <DialogContent class="sm:max-w-lg">
            <form
                @submit.prevent="handleUpload"
                class="flex max-h-full flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>
                        {{ t('vouchers.select.mark_as_missing_confirm.title') }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <DialogDescription class="flex flex-col gap-2">
                        <i18n-t
                            keypath="vouchers.select.mark_as_missing_confirm.content"
                            tag="span"
                            scope="global"
                        >
                            <template #body>
                                <span class="font-bold">
                                    {{ voucherStore.validVoucherNumbers }}
                                </span>
                            </template>
                        </i18n-t>
                        {{ t('vouchers.select.mark_as_missing_confirm.description') }}
                    </DialogDescription>

                    <VoucherMarkAsMissingForm
                        :form="form"
                        :company-id="companyId"
                    />
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="form.resetAndClearErrors()"
                        >
                            {{ t('forms.cancel') }}
                        </Button>
                    </DialogClose>

                    <Button
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                    >
                        {{ t('forms.yes') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
